import * as React from "react";
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CardHeader,
	Divider,
	MenuItem,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { LockKeyIcon } from "@phosphor-icons/react/dist/ssr/LockKey";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { toast } from "sonner";

import { formatBytes, statusCode } from "@/lib/admin-formatters";
import { languageLabel, LANGUAGES } from "@/lib/languages";
import type { Voice, VoicePayload } from "@/hooks/api/use-voices";
import {
	MAX_VOICE_SAMPLE_BYTES,
	useAdminVoices,
	useCreateVoice,
	useDeleteVoice,
	useGenerateVoiceSample,
	useRemoveVoiceSample,
	useReorderVoices,
	useUpdateVoice,
	useUploadVoiceSample,
} from "@/hooks/api/use-voices";
import { useDialog } from "@/hooks/use-dialog";
import { useAuth } from "@/components/core/auth-provider";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { groupByLanguage, nextPosition, reorderWithin } from "./ordering";
import { VoiceCard } from "./voice-card";
import { VoiceFormDrawer } from "./voice-form-drawer";

/**
 * The voice catalogue: the short list of voices every client picks from.
 *
 * Platform-level and super-admin only, the same shape as the price book — `voice` is a
 * public-schema table, so one row here changes what every workspace in the product is offered.
 * That is stated on the screen in a line rather than explained in a panel, because it is the one
 * thing about this page that is not obvious from looking at it.
 *
 * Curation is the feature. A provider's own catalogue runs to thousands of voices named "Rachel
 * (accent: american, age: young, use case: narration)"; what a client is offered is four or five
 * per language, under names we chose, in an order we chose, each with a sample a client can hear.
 * Everything on this screen exists to make that list, and the order it is in is a decision rather
 * than an accident — which is why reordering is a first-class control and not a hidden numeric
 * field.
 */
export function Voices(): React.JSX.Element {
	const { user } = useAuth();
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	const [language, setLanguage] = React.useState("");
	const filters = React.useMemo(() => ({ language: language || null }), [language]);

	const { data, isLoading, isError, error, refetch } = useAdminVoices(filters, isSuperAdmin);

	const createMutation = useCreateVoice();
	const updateMutation = useUpdateVoice();
	const reorderMutation = useReorderVoices();
	const uploadMutation = useUploadVoiceSample();
	const removeSampleMutation = useRemoveVoiceSample();
	const deleteMutation = useDeleteVoice();
	const generateMutation = useGenerateVoiceSample();

	const busy =
		createMutation.isPending ||
		updateMutation.isPending ||
		reorderMutation.isPending ||
		uploadMutation.isPending ||
		removeSampleMutation.isPending ||
		generateMutation.isPending ||
		deleteMutation.isPending;

	/**
	 * The catalogue as it is on screen, which is the server's answer until somebody drags.
	 *
	 * A reorder is written and then re-read, and between those two the list would snap back to the
	 * old order if it were rendered straight from the query. Holding it here means the drag stays
	 * where it was dropped; the refetch that follows either write or refusal overwrites it, which
	 * is why `useReorderVoices` invalidates in `onSettled`.
	 */
	const [rows, setRows] = React.useState<Voice[]>([]);

	React.useEffect(() => {
		setRows(data ?? []);
	}, [data]);

	const groups = React.useMemo(() => groupByLanguage(rows), [rows]);

	const [drawerOpen, setDrawerOpen] = React.useState(false);
	const [editing, setEditing] = React.useState<Voice | null>(null);

	const {
		data: removeTarget,
		open: isRemoveOpen,
		handleOpen: openRemove,
		handleClose: closeRemove,
	} = useDialog<Voice>();

	const sensors = useSensors(
		// A few pixels of travel before a drag starts, so the buttons inside a row still click.
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const commitOrder = (tag: string, ids: string[]): void => {
		setRows((current) => reorderWithin(current, tag, ids));
		reorderMutation.mutate(ids);
	};

	const handleDragEnd = (tag: string, event: DragEndEvent): void => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const ids = groups.find((group) => group.language === tag)?.voices.map((voice) => voice.id) ?? [];
		const from = ids.indexOf(String(active.id));
		const to = ids.indexOf(String(over.id));
		if (from === -1 || to === -1) return;

		commitOrder(tag, arrayMove(ids, from, to));
	};

	const handleMove = (voice: Voice, offset: -1 | 1): void => {
		const ids = groups.find((group) => group.language === voice.language)?.voices.map((row) => row.id) ?? [];
		const from = ids.indexOf(voice.id);
		const to = from + offset;
		if (from === -1 || to < 0 || to >= ids.length) return;

		commitOrder(voice.language, arrayMove(ids, from, to));
	};

	/**
	 * Write the form, and answer with the row as it now stands.
	 *
	 * Shared by saving and generating, because generating has to save first: the sample is stored
	 * against a row, and a script the provider has just spoken that is not the script on the row
	 * is a voice whose demo says something nobody can find. It also means the generate button
	 * works on a voice that does not exist yet — the alternative is telling somebody to save,
	 * close and reopen before they are allowed to listen.
	 */
	const persist = async (payload: VoicePayload): Promise<Voice> => {
		if (editing) return await updateMutation.mutateAsync({ id: editing.id, data: payload });

		const created = await createMutation.mutateAsync({
			...payload,
			// A new voice lands at the end of its language's list rather than in front of
			// voices somebody already ordered. Where the language filter hides that list this
			// is a guess, and a wrong guess costs one drag.
			position: nextPosition(rows, payload.language),
		});

		// The row exists from here on, so the drawer is editing it. Without this a sample the API
		// refuses leaves an open form still saying "Add voice" over a voice that is already
		// curated, and pressing it again curates a second one.
		setEditing(created);

		return created;
	};

	const handleSave = async (payload: VoicePayload, sampleFile: File | null): Promise<void> => {
		try {
			const saved = await persist(payload);

			// The recording chosen on the form. It goes after the row exists, because the bytes are
			// stored under the row's own sample id — there is nowhere to put a file for a voice
			// that has not been created yet.
			if (sampleFile) await uploadMutation.mutateAsync({ id: saved.id, file: sampleFile });

			setDrawerOpen(false);
			setEditing(null);
		} catch {
			// The mutation toasts the API's own refusal — a duplicate provider voice id for a
			// language is the one people hit, and the wording matters. Keep the form open.
		}
	};

	/**
	 * Save the form, then have the provider speak the script.
	 *
	 * The drawer stays open and takes the fresh row back: the point of pressing this is to listen
	 * to what came out and word it differently, which is not a thing you can do on a screen that
	 * just closed. A refusal is left on the mutation for the drawer to render in place — the one
	 * outcome that must never look like a sample.
	 */
	const handleGenerate = async (payload: VoicePayload): Promise<void> => {
		try {
			const saved = await persist(payload);

			// Not `setEditing(...)` on the way back: this can take a minute, and the drawer may be
			// showing a different voice by the time it answers — re-seeding it then would throw
			// away what somebody is typing into that one. `persist` has already set `editing`, and
			// the drawer reads the row out of `rows`, which the mutation invalidates before it
			// resolves.
			await generateMutation.mutateAsync({ id: saved.id, script: payload.sampleScript ?? undefined });
		} catch {
			// Saving toasts its own refusal; generating shows the provider's inside the drawer.
			// Either way the form stays as it is, with what was typed still in it.
		}
	};

	const handleUploadSample = async (voice: Voice, file: File): Promise<void> => {
		if (file.size > MAX_VOICE_SAMPLE_BYTES) {
			toast.error(`${file.name} is over the ${formatBytes(MAX_VOICE_SAMPLE_BYTES)} limit`);
			return;
		}

		try {
			await uploadMutation.mutateAsync({ id: voice.id, file });
		} catch {
			// 413 over the ceiling, 415 where the bytes are not audio a browser can play; both toast.
		}
	};

	const handleRemove = async (): Promise<void> => {
		if (!removeTarget) return;

		try {
			await deleteMutation.mutateAsync(removeTarget.id);
		} finally {
			closeRemove();
		}
	};

	const forbidden = !isSuperAdmin || statusCode(error) === 403;

	if (forbidden) {
		return (
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={3}>
					<Typography variant="h4">Voices</Typography>
					<Alert severity="warning" icon={<LockKeyIcon />}>
						<AlertTitle>This screen is for platform administrators</AlertTitle>
						<Typography variant="body2">
							The voice list is the same for everyone, so it is set here rather than per account. You choose your own
							campaign&rsquo;s voice on step 2 of a campaign request, where you can hear each one.
						</Typography>
					</Alert>
				</Stack>
			</Box>
		);
	}

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Voices</Typography>
							{/* The one thing about this page that looking at it will not tell you. */}
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Curated once for the whole platform — every account is offered this list, in this order.
							</Typography>
						</Box>

						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							{/* No label. Every value this can hold is the name of a language, starting with
							    "All languages" — a caption above it saying "Language" would be the control
							    reading itself out. */}
							<TextField
								select
								size="small"
								// On the input rather than the TextField: unknown props land on the root
								// `FormControl` div, where an `aria-label` names nothing.
								slotProps={{ htmlInput: { "aria-label": "Filter by language" } }}
								value={language}
								onChange={(event) => setLanguage(event.target.value)}
								sx={{ minWidth: 180 }}
							>
								<MenuItem value="">All languages</MenuItem>
								{LANGUAGES.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>

							<Button
								startIcon={<PlusIcon />}
								variant="contained"
								onClick={() => {
									setEditing(null);
									setDrawerOpen(true);
								}}
							>
								Add Voice
							</Button>
						</Stack>
					</Stack>

					{isError && statusCode(error) !== 403 ? (
						<Alert
							severity="error"
							action={
								<Button color="inherit" size="small" onClick={() => void refetch()}>
									Try Again
								</Button>
							}
						>
							The catalogue could not be loaded.
						</Alert>
					) : null}

					{isLoading ? (
						<Card>
							<Stack spacing={1} sx={{ p: 2 }}>
								<Skeleton height={44} variant="rounded" />
								<Skeleton height={44} variant="rounded" />
								<Skeleton height={44} variant="rounded" />
							</Stack>
						</Card>
					) : null}

					{!isLoading && !isError && groups.length === 0 ? (
						<Card>
							<Stack spacing={2} sx={{ alignItems: "flex-start", p: 3 }}>
								<Stack spacing={0.5}>
									<Typography variant="h6">
										{language ? `No ${languageLabel(language)} voices yet` : "Nothing curated yet"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{language
											? "Nothing on offer in this language yet. Other languages may have some."
											: "A voice is a name you choose, something behind it — a provider’s voice or a recording — and a sample a client can listen to."}
									</Typography>
								</Stack>

								<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
									<Button
										startIcon={<PlusIcon />}
										variant="contained"
										onClick={() => {
											setEditing(null);
											setDrawerOpen(true);
										}}
									>
										{language ? `Add a ${languageLabel(language)} voice` : "Add the first voice"}
									</Button>
									{language ? <Button onClick={() => setLanguage("")}>Show All Languages</Button> : null}
								</Stack>
							</Stack>
						</Card>
					) : null}

					{groups.map((group) => (
						<Card key={group.language}>
							<CardHeader
								title={languageLabel(group.language)}
								// A count, not a sentence: how many of this language's voices a client can
								// actually be offered today.
								subheader={`${group.voices.filter((voice) => voice.isActive).length} on offer`}
								slotProps={{ subheader: { variant: "caption" } }}
							/>
							<Divider />

							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={(event) => handleDragEnd(group.language, event)}
							>
								<SortableContext items={group.voices.map((voice) => voice.id)} strategy={verticalListSortingStrategy}>
									<Stack divider={<Divider />}>
										{group.voices.map((voice, index) => (
											<VoiceCard
												key={voice.id}
												voice={voice}
												index={index}
												count={group.voices.length}
												busy={busy}
												onMove={handleMove}
												onToggleActive={(target, isActive) =>
													updateMutation.mutate({ id: target.id, data: { isActive } })
												}
												onUploadSample={(target, file) => void handleUploadSample(target, file)}
												onEdit={(target) => {
													setEditing(target);
													setDrawerOpen(true);
												}}
												onRemove={(target) => openRemove(target)}
											/>
										))}
									</Stack>
								</SortableContext>
							</DndContext>
						</Card>
					))}
				</Stack>
			</Box>

			<VoiceFormDrawer
				open={drawerOpen}
				// The live row rather than the snapshot taken when Edit was pressed: removing the
				// sample from inside the drawer has to change what the drawer shows.
				voice={editing ? (rows.find((row) => row.id === editing.id) ?? editing) : null}
				defaultLanguage={language || "en"}
				isSaving={createMutation.isPending || updateMutation.isPending || uploadMutation.isPending}
				isGenerating={generateMutation.isPending}
				generateError={generateMutation.error}
				onClose={() => {
					setDrawerOpen(false);
					setEditing(null);
					// The last refusal belongs to the drawer that was open, not to the next one.
					generateMutation.reset();
				}}
				onSave={(payload, sampleFile) => void handleSave(payload, sampleFile)}
				onGenerate={(payload) => void handleGenerate(payload)}
				onRemoveSample={(target) => removeSampleMutation.mutate(target.id)}
			/>

			<DeleteModal
				open={isRemoveOpen}
				onClose={closeRemove}
				onDelete={() => void handleRemove()}
				title={`Remove ${removeTarget?.displayName ?? "this voice"}?`}
				description="It comes off every account's catalogue immediately."
				deleteText="Remove"
				warningText="A voice a request already chose is retired rather than deleted, so that request still shows what it picked."
			/>
		</React.Fragment>
	);
}
